import type { CSSProperties } from 'react'
import {
  ArrowRight, CheckCircle2, CloudOff, Clock, Clapperboard, GraduationCap,
  Puzzle, Sparkles,
} from 'lucide-react'
import type { Ik, Voortgang } from '../../gedeeld/types'
import type { Cursus } from '../lib/types'
import { CURSUSSEN } from '../cursussen'

/* ===========================================================================
 *  Het overzicht
 *
 *  De vraag die dit scherm beantwoordt is niet "wat is er allemaal" maar
 *  "wat moet ik nog". Vandaar dat de stand op de kaart staat en niet in een
 *  apart blokje ernaast: je ziet in één oogopslag wat er open staat.
 * =========================================================================== */

export default function Overzicht({
  ik, voortgang, bewaard, opCursus,
}: {
  ik: Ik
  voortgang: Voortgang[]
  bewaard: boolean
  opCursus: (id: string) => void
}) {
  const open = CURSUSSEN.filter((c) => !isKlaar(c, voortgang))

  return (
    <main className="midden">
      <div className="groet">
        <div className="hoi">{begroeting()}</div>
        <h1>{voornaam(ik.naam)}</h1>
        <p className="onder">
          {open.length === 0
            ? 'Je bent bij. Er staat op dit moment niets open.'
            : open.length === 1
              ? 'Er staat één cursus voor je klaar.'
              : `Er staan ${open.length} cursussen voor je klaar.`}
        </p>
      </div>

      {!bewaard && (
        <div className="niet-bewaard">
          <CloudOff size={16} />
          <span>
            Je voortgang staat nu alleen op dit apparaat. Zodra er weer
            verbinding is, gaat hij vanzelf mee.
          </span>
        </div>
      )}

      <div className="kopje">Cursussen</div>

      <div className="cursusrij">
        {CURSUSSEN.map((cursus, i) => (
          <Kaart
            key={cursus.id}
            cursus={cursus}
            stand={standVan(cursus, voortgang)}
            vertraging={i * 60}
            opKlik={() => opCursus(cursus.id)}
          />
        ))}
      </div>

      <div className="voet-tekst">
        Truckwash1 Academy · je voortgang wordt bewaard bij je dossier.
        Klopt er iets niet in een cursus? Geef het door — lesmateriaal hoort
        gecorrigeerd te kunnen worden.
      </div>
    </main>
  )
}

/* ------------------------------------------------------------------ *
 *  De kaart
 * ------------------------------------------------------------------ */

interface Stand {
  gedaan: number
  totaal: number
  geslaagd: boolean
  score: number | null
  begonnen: boolean
}

function Kaart({
  cursus, stand, vertraging, opKlik,
}: {
  cursus: Cursus
  stand: Stand
  vertraging: number
  opKlik: () => void
}) {
  /* Wat er in deze cursus zit. Uit de inhoud geteld en niet met de hand
     bijgehouden -- een getal dat je met de hand ophoogt is een getal dat je
     vergeet, en dan staat er "3 filmpjes" bij een cursus met vijf. */
  const films = cursus.modules
    .flatMap((m) => m.blokken).filter((b) => b.soort === 'film').length
  const opdrachten = cursus.modules
    .flatMap((m) => m.blokken)
    .filter((b) => b.soort === 'sorteer' || b.soort === 'spot'
      || b.soort === 'keuze' || b.soort === 'prompt').length

  return (
    <button
      type="button"
      className={`cursuskaart opkomst accent-${cursus.accent}`}
      onClick={opKlik}
      style={{ '--vertraging': `${vertraging}ms` } as CSSProperties}
    >
      <div className="bovenaan">
        <span className="vlag"><GraduationCap size={23} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2>{cursus.titel}</h2>
          <div className="sub">{cursus.ondertitel}</div>
        </div>
        <Speld stand={stand} />
      </div>

      <p className="belofte">{cursus.belofte}</p>

      {/*
        * Dezelfde rail als in de les, in het klein.
        *
        * Dit stond eerst als één doorlopende balk. Een balk zegt "je bent op
        * 43%", en daar kun je niets mee; deze zegt welke stations je gehad
        * hebt -- hetzelfde beeld dat je straks bovenaan de les terugziet.
        */}
      <div className="ministrip" aria-hidden>
        {cursus.modules.map((m, i) => (
          <i key={m.id} className={i < stand.gedaan ? 'gedaan' : ''} />
        ))}
        <i className={`slot ${stand.geslaagd ? 'gedaan' : ''}`} />
      </div>

      <div className="voet">
        <span className="los"><Clock size={14} /> {cursus.duurMinuten} min</span>
        {films > 0 && (
          <span className="los"><Clapperboard size={14} /> {films} filmpjes</span>
        )}
        {opdrachten > 0 && (
          <span className="los"><Puzzle size={14} /> {opdrachten} opdrachten</span>
        )}
        {stand.geslaagd && stand.score !== null && (
          <span className="los"><CheckCircle2 size={14} /> {stand.score}% gehaald</span>
        )}
        <span className="ga">
          {stand.geslaagd ? 'Teruglezen' : stand.begonnen ? 'Verder' : 'Beginnen'}
          <ArrowRight size={15} />
        </span>
      </div>
    </button>
  )
}

function Speld({ stand }: { stand: Stand }) {
  if (stand.geslaagd) {
    return <span className="speld klaar"><CheckCircle2 size={13} /> Gehaald</span>
  }
  if (stand.begonnen) {
    return <span className="speld bezig">Bezig</span>
  }
  return <span className="speld nieuw"><Sparkles size={13} /> Nieuw</span>
}

/* ------------------------------------------------------------------ *
 *  Rekenwerk
 * ------------------------------------------------------------------ */

export function standVan(cursus: Cursus, voortgang: Voortgang[]): Stand {
  const v = voortgang.find((x) => x.cursus === cursus.id && x.versie === cursus.versie)
  const gedaan = v ? cursus.modules.filter((m) => v.lessen.includes(m.id)).length : 0
  return {
    gedaan,
    totaal: cursus.modules.length,
    geslaagd: !!v?.geslaagdAt,
    score: v?.besteScore ?? null,
    begonnen: gedaan > 0 || (v?.pogingen ?? 0) > 0,
  }
}

function isKlaar(cursus: Cursus, voortgang: Voortgang[]): boolean {
  return standVan(cursus, voortgang).geslaagd
}

function voornaam(naam: string): string {
  const eerste = naam.trim().split(/\s+/)[0]
  return eerste && eerste.length > 1 ? eerste : (naam || 'Welkom')
}

/**
 * Goedemorgen, goedemiddag, goedenavond.
 *
 * Op de klok van het toestel en niet op die van de server. Die van het
 * toestel is de klok waar de gebruiker naar kijkt, en dit is een begroeting
 * en geen tijdregistratie.
 */
function begroeting(): string {
  const u = new Date().getHours()
  if (u < 6) return 'Goedenacht'
  if (u < 12) return 'Goedemorgen'
  if (u < 18) return 'Goedemiddag'
  return 'Goedenavond'
}
